import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Plus, ChevronDown, ChevronUp } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { peopleService } from '../../services/peopleService';
import { labourService } from '../../services/labourService';
import api from '../../services/api';
import BulkAssignModal from './BulkAssignModal';
import MantleDataGrid, { gcell } from '../../components/MantleDataGrid';
import { FilterPopover } from '../../components/gridFilters';
import GridPager from '../../components/GridPager';

// =============================================================================
// Structures — the nominal roll, wired to bud_structures. One row per budgeted
// position. Job Title is chosen from the Job Titles catalogue (bud_job_titles),
// which drives the Grade automatically. A position can be marked Vacant; when it
// is, the basic salary is set to the Mid of its grade's band (from Grades).
// Pay figures are ENCRYPTED at rest.
//
// Rendering: Glide Data Grid via MantleDataGrid (canvas-painted — interactions
// repaint pixels instead of re-rendering React components, so it stays instant
// at any row count). Range copy/paste (Ctrl+C / Ctrl+V) and search (Ctrl+F)
// come from the wrapper. This page is the reference integration for moving
// other grids (e.g. OPEX) onto MantleDataGrid.
//
// Version comes from the top bar. Site too — "All sites" shows every site's rows
// (each keeps its own site) with a Site column.
// =============================================================================

// ON-COST RATES COME FROM THE SERVER, NOT FROM CONSTANTS HERE.
//
// These were `const UIF_PCT = 1.0` in this file, and peopleController carried a
// comment warning that duplicating the formula is how the two drift apart. It
// was not hypothetical: calc_people_costs now posts salaries to the ledger, so
// a rate that differs between this page and that calc means the number on
// screen and the number in the P&L disagree with nothing to say so.
//
// md_config.payroll_oncosts is the one source. These defaults are only used
// before the fetch returns, and they match the config's own defaults.
const DEFAULT_ONCOSTS = { uif_pct: 1.0, sdl_pct: 1.0, uif_monthly_cap: null };

// UIF is capped in reality; a null cap means uncapped, which is what the
// original hard-coded formula did.
const uifSdlWith = (o) => (p) => {
  const basic = Number(p.basic || 0);
  const uifBase = o.uif_monthly_cap === null || o.uif_monthly_cap === undefined
    ? basic : Math.min(basic, o.uif_monthly_cap);
  return (uifBase * o.uif_pct) / 100 + (basic * o.sdl_pct) / 100;
};
const finalCCWith = (o) => (p) =>
  Number(p.basic || 0) + Number(p.allowance || 0) + Number(p.medical || 0) + uifSdlWith(o)(p);
const fmtR = (n) => `R ${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtN = (n) => Math.round(Number(n) || 0).toLocaleString();

const inputClass =
  'w-full text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 px-2.5 py-2 outline-none focus:ring-2 focus:ring-slate-400';

const newId = () =>
  (window.crypto?.randomUUID ? window.crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0; const v = c === 'x' ? r : (r & 0x3) | 0x8; return v.toString(16);
      }));

const BLANK_ENTRY = { site_code: '', name: '', title: '', grade: '', pay_type: 'Fixed', fulltime: true, fnKey: '', shift_no: '' };
// A function is only unique WITH its module, so the picker value carries both.
const fnKeyOf = (moduleCode, functionCode) => (functionCode ? `${moduleCode}|${functionCode}` : '');
const splitFnKey = (key) => { const [m, f] = String(key || '').split('|'); return { module_code: m || '', function_code: f || '' }; };
const NUM_FIELDS = ['basic', 'allowance', 'leave', 'medical'];

// Per-cell theme accents (work in light + dark).
const T_VACANT = { textDark: '#dc2626', bgCell: 'rgba(220, 38, 38, 0.10)' };
const T_FILLED = { textDark: '#059669', bgCell: 'rgba(5, 150, 105, 0.10)' };
const T_TOTAL = { bgCell: 'rgba(100, 116, 139, 0.22)', baseFontStyle: '600 12px' };
const T_CALC = { bgCell: 'rgba(100, 116, 139, 0.10)' };
const T_MUTED = { textDark: '#94a3b8' };

const Structures = () => {
  const { selectedVersion, selectedSite } = useAppContext();
  // The on-cost rates, fetched once. Until they arrive the defaults match the
  // config's own, so the grid never renders a wrong number — only a briefly
  // stale one, and only if somebody has changed the rates.
  const [oncosts, setOncosts] = useState(DEFAULT_ONCOSTS);
  const [costState, setCostState] = useState(null);   // what is in the ledger
  const [recalcing, setRecalcing] = useState(false);
  const [people, setPeople] = useState([]);
  const [jobTitles, setJobTitles] = useState([]);   // [{ title, grade_code }]
  const [gradeMid, setGradeMid] = useState({});      // { grade_code: mid }
  const [sites, setSites] = useState([]);            // [{ code, name }]
  const [dirty, setDirty] = useState({});
  const [catalog, setCatalog] = useState({ functions: [], shifts: [] }); // labour functions + named crews
  const [addOpen, setAddOpen] = useState(false);        // the add line is collapsed by default
  const [selRows, setSelRows] = useState(() => new Set()); // grid row indices in the selection
  const [bulkOpen, setBulkOpen] = useState(false);
  const [titleEditor, setTitleEditor] = useState(null); // { id, x, y } — job-title picker
  const [pickEditor, setPickEditor] = useState(null);   // { kind: 'site'|'function'|'shift', id, x, y }
  const [filterUI, setFilterUI] = useState(null);       // { colId, x, y, options } — column filter popover
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [entry, setEntry] = useState(BLANK_ENTRY);

  const ready = !!selectedVersion;
  const allSites = ready && !selectedSite;

  // Mirror of `people` for callbacks that need to READ rows without a `people`
  // dependency (keeps handler identities stable).
  const peopleRef = useRef([]);
  peopleRef.current = people;

  const siteName = useCallback((code) => sites.find((s) => s.code === code)?.name || code || '—', [sites]);

  // ---- Labour catalogue lookups ----------------------------------------------
  // Functions are per site; crews are per function. A position can only be on a
  // crew that exists inside its own function, so the shift list is derived from
  // whatever function the row currently holds.
  const fnsForSite = useCallback(
    (site) => catalog.functions.filter((f) => f.site_code === site), [catalog]);
  const crewsFor = useCallback(
    (site, mod, fn) => catalog.shifts.filter(
      (s) => s.site_code === site && s.module_code === mod && s.function_code === fn), [catalog]);
  const fnNameOf = useCallback((p) => {
    if (!p.function_code) return '—';
    const f = catalog.functions.find((x) => x.site_code === p.site_code
      && x.module_code === p.module_code && x.function_code === p.function_code);
    return f ? f.function_name : p.function_code;
  }, [catalog]);
  const crewNameOf = useCallback((p) => {
    if (!p.shift_no) return '—';
    const s = catalog.shifts.find((x) => x.site_code === p.site_code && x.module_code === p.module_code
      && x.function_code === p.function_code && Number(x.shift_no) === Number(p.shift_no));
    return s ? s.shift_name : `Shift ${p.shift_no}`;
  }, [catalog]);

  // ---- Per-column filters (header menu ▾) ------------------------------------
  // Accessors give each filterable column its display string per row.
  const accessors = useMemo(() => ({
    site: (p) => siteName(p.site_code),
    name: (p) => p.name,
    title: (p) => p.title,
    grade: (p) => p.grade || '—',
    status: (p) => (p.is_vacant ? 'Vacant' : 'Filled'),
    pay_type: (p) => p.pay_type,
    func: (p) => fnNameOf(p),
    crew: (p) => crewNameOf(p),
    fulltime: (p) => (p.fulltime ? 'Full-time' : 'Part-time'),
  }), [siteName, fnNameOf, crewNameOf]);
  // Filtering runs in PostgreSQL, not over an in-memory array: the nominal roll
  // of a large employer is tens of thousands of positions and the grid now only
  // ever holds one page of them. `accessors` stays — the cell renderers use it —
  // but it no longer decides which rows exist.
  const [filters, setFilters] = useState({});
  const [pageInfo, setPageInfo] = useState({ total: null, hasMore: false });
  const [serverSummary, setServerSummary] = useState(null);
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(100);

  const activeIds = useMemo(() => Object.keys(filters), [filters]);

  // Changing page or filter refetches, and the refetch clears staged edits.
  // Silently throwing away someone's typing is not acceptable, so ask first.
  const dirtyCountRef = useRef(0);
  const confirmDiscard = useCallback(() => {
    const n = dirtyCountRef.current;
    if (n === 0) return true;
    return window.confirm(
      `${n} unsaved change${n === 1 ? '' : 's'} will be discarded when the rows reload. Save first, or continue and lose them?`
    );
  }, []);
  // Any filter change returns to page 1 — a filter matching three positions,
  // applied while on page 6, would otherwise look like it found nothing.
  const setFilter = useCallback((colId, f) => {
    if (!confirmDiscard()) return;
    setOffset(0);
    setFilters((prev) => {
      const next = { ...prev };
      if (!f || (!f.text && !f.values)) delete next[colId];
      else next[colId] = f;
      return next;
    });
  }, [confirmDiscard]);
  const clearAll = useCallback(() => { if (!confirmDiscard()) return; setOffset(0); setFilters({}); }, [confirmDiscard]);

  // The server already filtered — these ARE the matching rows for this page.
  const filtered = people;

  // Mirror of the FILTERED rows — grid row indices map to this array.
  const filteredRef = useRef([]);
  filteredRef.current = filtered;

  const titleToGrade = useMemo(() => {
    const m = new Map();
    for (const j of jobTitles) m.set(j.title, j.grade_code || '');
    return m;
  }, [jobTitles]);
  const midFor = useCallback((grade) => Number(gradeMid[grade] || 0), [gradeMid]);

  // The on-cost rates and what is currently in the ledger. Separate from
  // loadData because it does not change when you page or filter the roll.
  const loadCostState = useCallback(async () => {
    if (!ready) return;
    try {
      const res = await peopleService.getPeopleCosts();
      if (res?.data?.oncosts) setOncosts(res.data.oncosts);
      setCostState(res?.data || null);
    } catch (e) {
      // Not fatal: the roll is still editable and the defaults match config's.
      // Only the ledger panel is unavailable.
      setCostState(null);
    }
  }, [ready]);

  const recalculate = useCallback(async () => {
    setRecalcing(true);
    try {
      await peopleService.calculatePeopleCosts();
      await loadCostState();
    } catch (e) {
      setError(e.response?.data?.message || 'Could not recalculate people cost.');
    } finally {
      setRecalcing(false);
    }
  }, [loadCostState]);

  const loadData = useCallback(async () => {
    if (!ready) { setPeople([]); return; }
    setIsLoading(true); setError(null);
    try {
      const [structRes, jtRes, gradeRes, catRes] = await Promise.all([
        peopleService.getStructures({ filters, limit, offset }),
        peopleService.getJobTitles(),
        peopleService.getGrades(),
        // The labour catalogue is a nice-to-have: if it fails, the roll still
        // loads and the Function / Crew columns simply show nothing to pick.
        labourService.getCatalog().catch(() => null),
      ]);
      if (!structRes.success) throw new Error(structRes.message);
      setPeople(structRes.data.positions.map((p) => ({
        position_id: p.position_id,
        site_code: p.site_code,
        name: p.employee_name || '',
        title: p.position_title,
        grade: p.grade_code || '',
        is_vacant: !!p.is_vacant,
        pay_type: p.pay_type || 'Fixed',
        module_code: p.module_code || '',
        function_code: p.function_code || '',
        shift_no: p.shift_no || null,
        fulltime: p.is_fulltime,
        basic: Number(p.basic) || 0,
        allowance: Number(p.allowance) || 0,
        leave: Number(p.leave_days) || 0,
        medical: Number(p.medical) || 0,
        isNew: false,
      })));
      setJobTitles(jtRes?.success ? (jtRes.data.jobTitles || []) : []);
      setCatalog(catRes?.success
        ? { functions: catRes.data.functions || [], shifts: catRes.data.shifts || [] }
        : { functions: [], shifts: [] });
      const midMap = {};
      if (gradeRes?.success) for (const g of gradeRes.data) midMap[g.grade_code] = g.mid;
      setGradeMid(midMap);
      setPageInfo({ total: structRes.data.total ?? null, hasMore: !!structRes.data.hasMore });
      setServerSummary(structRes.data.summary ?? null);
      setDirty({});
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load positions.');
      setPeople([]);
      setPageInfo({ total: null, hasMore: false });
    } finally { setIsLoading(false); }
  }, [ready, filters, limit, offset]);

  // Debounced so the filter popover's contains-box does not fire a query per
  // keystroke.
  useEffect(() => {
    const t = setTimeout(loadData, 250);
    return () => clearTimeout(t);
  }, [loadData]);

  // Not debounced with loadData: the rates and the ledger state do not change
  // when you page or filter the roll.
  useEffect(() => { loadCostState(); }, [loadCostState]);

  useEffect(() => {
    let cancelled = false;
    if (!ready) return;
    api.get('/sites/load-all')
      .then((r) => { if (!cancelled) setSites(Array.isArray(r.data) ? r.data : []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [ready]);

  // Header figures cover the WHOLE filtered set, not the page. The server sends
  // component totals (it has to decrypt to get them) and the UIF/SDL formula is
  // applied here, so the percentages live in exactly one place. Falls back to
  // the loaded rows when the server did not send a summary — i.e. when paging
  // is off and the page really does hold everything.
  const uifSdl = useMemo(() => uifSdlWith(oncosts), [oncosts]);
  const finalCC = useMemo(() => finalCCWith(oncosts), [oncosts]);

  const summary = useMemo(() => {
    if (serverSummary) {
      const monthly = serverSummary.basic + serverSummary.allowance + serverSummary.medical
        + (serverSummary.basic * (oncosts.uif_pct + oncosts.sdl_pct)) / 100;
      return { positions: serverSummary.positions, vacancies: serverSummary.vacancies, monthly };
    }
    return {
      positions: people.length,
      vacancies: people.filter((p) => p.is_vacant).length,
      monthly: people.reduce((a, p) => a + finalCC(p), 0),
    };
  }, [serverSummary, people, oncosts, finalCC]);

  // Totals reflect what's on screen (the filtered rows).
  const totals = useMemo(() => filtered.reduce((acc, p) => {
    acc.basic += Number(p.basic || 0);
    acc.allowance += Number(p.allowance || 0);
    acc.leave += Number(p.leave || 0);
    acc.uifSdl += uifSdl(p);
    acc.medical += Number(p.medical || 0);
    acc.finalCC += finalCC(p);
    return acc;
  }, { basic: 0, allowance: 0, leave: 0, uifSdl: 0, medical: 0, finalCC: 0 }), [filtered, uifSdl, finalCC]);

  // ---- Row mutation handlers (stable identities) ------------------------------
  const onField = useCallback((id, field, value) => {
    setPeople((prev) => prev.map((p) => (p.position_id === id ? { ...p, [field]: value } : p)));
    setDirty((prev) => ({ ...prev, [id]: true }));
  }, []);

  const onTitle = useCallback((id, title) => {
    setPeople((prev) => prev.map((p) => {
      if (p.position_id !== id) return p;
      const grade = titleToGrade.get(title) ?? p.grade;
      return { ...p, title, grade, basic: p.is_vacant ? midFor(grade) : p.basic };
    }));
    setDirty((prev) => ({ ...prev, [id]: true }));
    setTitleEditor(null);
  }, [titleToGrade, midFor]);

  const onToggleVacant = useCallback((id) => {
    setPeople((prev) => prev.map((p) => {
      if (p.position_id !== id) return p;
      if (!p.is_vacant) {
        // Becoming vacant: clear the employee and set basic to the grade Mid.
        return { ...p, is_vacant: true, name: '', basic: midFor(p.grade) };
      }
      return { ...p, is_vacant: false };
    }));
    setDirty((prev) => ({ ...prev, [id]: true }));
  }, [midFor]);

  const onDelete = useCallback(async (id) => {
    const p = peopleRef.current.find((x) => x.position_id === id);
    if (p?.isNew) {
      setPeople((prev) => prev.filter((x) => x.position_id !== id));
      setDirty((prev) => { const d = { ...prev }; delete d[id]; return d; });
      return;
    }
    if (!window.confirm(`Delete position ${p?.title || ''}${p?.name ? ` (${p.name})` : ''}?`)) return;
    setError(null);
    try { await peopleService.deleteStructure(id); await loadData(); }
    catch (e) { setError(e.response?.data?.message || 'Failed to delete position.'); }
  }, [loadData]);

  // ---- Glide grid wiring -------------------------------------------------------
  // Filterable columns get a header menu (▾); a dot marks an active filter.
  const gcols = useMemo(() => {
    // `grow` lets Name/Job Title (and Site in All-Sites view) stretch to absorb
    // any leftover width, so the grid always fills the screen with no blank
    // strip after the last column.
    const base = [
      ...(allSites ? [{ id: 'site', title: 'Site', width: 120, grow: 0.5 }] : []),
      { id: 'name', title: 'Name', width: 150, grow: 1 },
      { id: 'title', title: 'Job Title', width: 170, grow: 1 },
      { id: 'grade', title: 'Grade', width: 64 },
      { id: 'status', title: 'Status', width: 76 },
      { id: 'pay_type', title: 'Pay Type', width: 84 },
      { id: 'func', title: 'Function', width: 130 },
      { id: 'crew', title: 'Shift', width: 110 },
      { id: 'fulltime', title: 'Full-time', width: 76 },
      { id: 'basic', title: 'Basic', width: 92 },
      { id: 'allowance', title: 'Allowance', width: 92 },
      { id: 'leave', title: 'Leave', width: 64 },
      { id: 'uifSdl', title: 'UIF + SDL', width: 86 },
      { id: 'medical', title: 'Medical', width: 86 },
      { id: 'finalCC', title: 'Final CC', width: 104 },
      { id: 'del', title: '', width: 36 },
    ];
    return base.map((c) => (accessors[c.id]
      ? { ...c, hasMenu: true, title: activeIds.includes(c.id) ? `${c.title} ●` : c.title }
      : c));
  }, [allSites, accessors, activeIds]);
  const colIds = useMemo(() => gcols.map((c) => c.id), [gcols]);

  // rowCount = visible (filtered) rows + 1 TOTAL row at the end.
  const rowCount = filtered.length + 1;

  const getCellContent = useCallback(([col, row]) => {
    const id = colIds[col];

    // TOTAL row (last row) — read-only, shaded, bold. Sums the visible rows.
    if (row === filtered.length) {
      if (id === 'name') {
        return gcell.text(activeIds.length ? 'TOTAL (filtered)' : 'TOTAL', { readonly: true, theme: T_TOTAL });
      }
      if (['basic', 'allowance', 'leave', 'uifSdl', 'medical', 'finalCC'].includes(id)) {
        return gcell.number(totals[id], { readonly: true, display: fmtN(totals[id]), theme: T_TOTAL });
      }
      return gcell.text('', { readonly: true, theme: T_TOTAL });
    }

    const p = filtered[row];
    if (!p) return gcell.text('', { readonly: true });

    switch (id) {
      // Editable in All-sites view: moving a position is how you get at another
      // site's functions, since a function only exists at one site.
      case 'site': return gcell.text(siteName(p.site_code), { readonly: true });
      case 'name':
        return p.is_vacant
          ? gcell.text('', { readonly: true })
          : gcell.text(p.name);
      case 'title': return gcell.text(p.title || '—', { readonly: true });
      case 'grade': return gcell.text(p.grade || '—', { readonly: true });
      case 'status':
        return gcell.text(p.is_vacant ? 'Vacant' : 'Filled', {
          readonly: true, theme: p.is_vacant ? T_VACANT : T_FILLED,
        });
      case 'pay_type': return gcell.text(p.pay_type, { readonly: true });
      // Both are pickers (click to open), never free text — a position may only
      // sit on a function that exists at its site, and a crew inside it.
      case 'func': return gcell.text(fnNameOf(p), { readonly: true, theme: p.function_code ? undefined : T_MUTED });
      case 'crew': return gcell.text(crewNameOf(p), { readonly: true, theme: p.shift_no ? undefined : T_MUTED });
      case 'fulltime': return gcell.boolean(p.fulltime);
      case 'basic': case 'allowance': case 'leave': case 'medical':
        return gcell.number(p[id], { display: fmtN(p[id]) });
      case 'uifSdl': return gcell.number(uifSdl(p), { readonly: true, display: fmtN(uifSdl(p)), theme: T_CALC });
      case 'finalCC': return gcell.number(finalCC(p), { readonly: true, display: fmtN(finalCC(p)), theme: T_TOTAL });
      case 'del': return gcell.text('✕', { readonly: true, theme: { textDark: '#94a3b8' } });
      default: return gcell.text('', { readonly: true });
    }
  }, [colIds, filtered, totals, siteName, activeIds, fnNameOf, crewNameOf, uifSdl, finalCC]);

  const onCellEdited = useCallback(([col, row], newVal) => {
    if (row >= filteredRef.current.length) return; // TOTAL row
    const id = colIds[col];
    const p = filteredRef.current[row];
    if (!p) return;
    if (id === 'name') { if (!p.is_vacant) onField(p.position_id, 'name', newVal.data ?? ''); return; }
    if (id === 'fulltime') { onField(p.position_id, 'fulltime', !!newVal.data); return; }
    if (NUM_FIELDS.includes(id)) { onField(p.position_id, id, Number(newVal.data) || 0); }
  }, [colIds, onField]);

  // Click actions: Job Title opens the picker; Status / del toggle & delete;
  // Pay Type flips Fixed ↔ Variable.
  const onCellClicked = useCallback(([col, row], event) => {
    if (row >= filteredRef.current.length) return;
    const id = colIds[col];
    const p = filteredRef.current[row];
    if (!p) return;
    if (id === 'title') {
      const b = event?.bounds;
      setTitleEditor({
        id: p.position_id,
        x: b ? b.x : (event?.localEventX ?? 200),
        y: b ? b.y + b.height : 200,
      });
    } else if (id === 'func' || id === 'crew' || id === 'site') {
      const b = event?.bounds;
      setPickEditor({
        kind: id === 'func' ? 'function' : id === 'crew' ? 'shift' : 'site',
        id: p.position_id,
        x: b ? b.x : (event?.localEventX ?? 200),
        y: b ? b.y + b.height : 200,
      });
    } else if (id === 'status') {
      onToggleVacant(p.position_id);
    } else if (id === 'pay_type') {
      onField(p.position_id, 'pay_type', p.pay_type === 'Fixed' ? 'Variable' : 'Fixed');
    } else if (id === 'del') {
      onDelete(p.position_id);
    }
  }, [colIds, onToggleVacant, onField, onDelete]);

  // Moving a position to another site invalidates its function AND crew — both
  // belong to one site — so they are cleared rather than left to fail the
  // foreign key on save.
  const onPickSite = useCallback((id, siteCode) => {
    setPeople((prev) => prev.map((p) => (p.position_id === id
      ? { ...p, site_code: siteCode, module_code: '', function_code: '', shift_no: null } : p)));
    setDirty((prev) => ({ ...prev, [id]: true }));
    setPickEditor(null);
  }, []);

  // ---- Grid selection → the rows a bulk assign will hit -----------------------
  // Row markers are off, so "selected rows" means every row the cell selection
  // spans (drag a column, or click then shift-click). Ctrl+A selects the lot.
  const onSelectionChanged = useCallback((s) => {
    const rows = new Set();
    const addRange = (r) => {
      if (!r) return;
      for (let y = r.y; y < r.y + r.height; y++) rows.add(y);
    };
    addRange(s?.current?.range);
    (s?.current?.rangeStack || []).forEach(addRange);
    // Honour row-marker selection too, in case markers are ever turned on.
    (s?.rows?.toArray?.() || []).forEach((y) => rows.add(y));
    setSelRows(rows);
  }, []);

  // The TOTAL row sits past the end of `filtered` — never include it.
  const selectedPositions = useMemo(
    () => [...selRows].filter((y) => y < filtered.length).map((y) => filtered[y]).filter(Boolean),
    [selRows, filtered]);

  // A function belongs to one site and a crew to one function, so a bulk change
  // can only offer those lists when the selection agrees on the level above.
  const commonSite = useMemo(() => {
    if (selectedPositions.length === 0) return null;
    const first = selectedPositions[0].site_code;
    return selectedPositions.every((p) => p.site_code === first) ? first : null;
  }, [selectedPositions]);

  const commonFn = useMemo(() => {
    if (selectedPositions.length === 0) return null;
    const { module_code, function_code } = selectedPositions[0];
    if (!function_code) return null;
    return selectedPositions.every((p) => p.module_code === module_code && p.function_code === function_code)
      ? { module_code, function_code } : null;
  }, [selectedPositions]);

  const applyBulk = useCallback((patch) => {
    const { site_code, fn, shift_no, title, pay_type, fulltime, is_vacant } = patch;
    const ids = new Set(selectedPositions.map((p) => p.position_id));
    setPeople((prev) => prev.map((p) => {
      if (!ids.has(p.position_id)) return p;
      const next = { ...p };

      // Order matters. Each level clears the one below it unless that level is
      // also being set in this same apply, in which case the explicit value wins.
      if (site_code !== undefined) {
        next.site_code = site_code;
        next.module_code = ''; next.function_code = ''; next.shift_no = null;
      }
      if (fn !== undefined) {
        next.module_code = fn ? fn.module_code : '';
        next.function_code = fn ? fn.function_code : '';
        next.shift_no = null;
      }
      if (shift_no !== undefined) next.shift_no = shift_no;

      // Title drives grade, and on a vacant position the basic follows the new
      // grade's Mid — same rule as the per-row job-title picker.
      if (title !== undefined) {
        const grade = titleToGrade.get(title) ?? next.grade;
        next.title = title;
        next.grade = grade;
        if (next.is_vacant) next.basic = midFor(grade);
      }
      if (pay_type !== undefined) next.pay_type = pay_type;
      if (fulltime !== undefined) next.fulltime = fulltime;

      // Only the fill -> vacant TRANSITION clears the name and resets basic, so
      // re-applying "vacant" to an already-vacant row leaves an edited basic be.
      if (is_vacant !== undefined) {
        if (is_vacant && !next.is_vacant) {
          next.is_vacant = true;
          next.name = '';
          next.basic = midFor(next.grade);
        } else if (!is_vacant) {
          next.is_vacant = false;
        }
      }
      return next;
    }));
    setDirty((prev) => {
      const d = { ...prev };
      ids.forEach((id) => { d[id] = true; });
      return d;
    });
    setBulkOpen(false);
  }, [selectedPositions, titleToGrade, midFor]);

  // Setting a function invalidates any crew from the old one, so clear it.
  const onPickFunction = useCallback((id, moduleCode, functionCode) => {
    setPeople((prev) => prev.map((p) => (p.position_id === id
      ? { ...p, module_code: moduleCode, function_code: functionCode, shift_no: null } : p)));
    setDirty((prev) => ({ ...prev, [id]: true }));
    setPickEditor(null);
  }, []);

  const onPickShift = useCallback((id, shiftNo) => {
    setPeople((prev) => prev.map((p) => (p.position_id === id ? { ...p, shift_no: shiftNo } : p)));
    setDirty((prev) => ({ ...prev, [id]: true }));
    setPickEditor(null);
  }, []);

  // Header ▾ menu → open the column's filter popover with its distinct values.
  // Options come from the server — the distinct values across the whole version
  // and site, not the page on screen. Fetched when the menu opens, because a
  // checklist of every employee name is not worth loading up front.
  const onHeaderMenu = useCallback(async (colIndex, bounds) => {
    const colId = colIds[colIndex];
    if (!accessors[colId]) return;
    const pos = { x: bounds ? bounds.x : 200, y: bounds ? bounds.y + bounds.height : 200 };
    setFilterUI({ colId, options: [], ...pos });
    try {
      const r = await peopleService.getStructureFacets(colId);
      if (r?.success) setFilterUI((prev) => (prev && prev.colId === colId ? { ...prev, options: r.data } : prev));
    } catch { /* the contains-box still works with an empty checklist */ }
  }, [colIds, accessors]);

  // ---- Inline add line ----------------------------------------------------------
  const setE = (k, v) => setEntry((prev) => ({ ...prev, [k]: v }));
  const onEntryTitle = (title) => setEntry((prev) => ({ ...prev, title, grade: titleToGrade.get(title) || '' }));

  const entryProblem =
    !entry.title ? 'Pick a job title.'
    : (allSites && !entry.site_code) ? 'Pick a site.'
    : '';

  const addPosition = () => {
    if (entryProblem) return;
    const site = allSites ? entry.site_code : selectedSite;
    const vacant = !entry.name.trim();
    const grade = entry.grade || '';
    const id = newId();
    const { module_code, function_code } = splitFnKey(entry.fnKey);
    setPeople((prev) => [...prev, {
      position_id: id, site_code: site, name: entry.name.trim(), title: entry.title, grade,
      is_vacant: vacant, pay_type: entry.pay_type, fulltime: entry.fulltime,
      module_code, function_code, shift_no: entry.shift_no ? Number(entry.shift_no) : null,
      basic: vacant ? midFor(grade) : 0, allowance: 0, leave: 0, medical: 0, isNew: true,
    }]);
    setDirty((prev) => ({ ...prev, [id]: true }));
    setEntry(BLANK_ENTRY);
  };

  // ---- Save ----------------------------------------------------------------------
  const hasChanges = Object.keys(dirty).length > 0;
  // Kept in a ref so confirmDiscard (defined above, and deliberately stable)
  // can read the live count without every consumer re-creating on each edit.
  dirtyCountRef.current = Object.keys(dirty).length;

  const handleSave = useCallback(async () => {
    setError(null);
    const payload = peopleRef.current.filter((p) => dirty[p.position_id]).map((p) => ({
      position_id: p.position_id,
      site_code: p.site_code,
      position_title: p.title,
      grade_code: p.grade,
      employee_name: p.name,
      is_vacant: p.is_vacant,
      pay_type: p.pay_type,
      module_code: p.module_code,
      function_code: p.function_code,
      shift_no: p.shift_no,
      is_fulltime: p.fulltime,
      leave_days: p.leave,
      basic: p.basic, allowance: p.allowance, medical: p.medical,
    }));
    if (payload.length === 0) return;
    setIsSaving(true);
    try {
      const res = await peopleService.saveStructures(payload);
      if (!res.success) throw new Error(res.message);
      await loadData();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to save positions.');
    } finally { setIsSaving(false); }
  }, [dirty, loadData]);

  const gridToolbar = useMemo(() => (
    <>
      {selectedPositions.length > 0 && (
        <button
          type="button"
          onClick={() => setBulkOpen(true)}
          title="Set site, function or shift on every selected position"
          className="px-3 py-1 rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
        >
          Assign {selectedPositions.length} selected…
        </button>
      )}
      {activeIds.length > 0 && (
        <button
          type="button"
          onClick={clearAll}
          className="px-3 py-1 rounded-md border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20"
        >
          Clear filters ({activeIds.length})
        </button>
      )}
      {/* SAVING THE ROLL DOES NOT MOVE THE LEDGER. Pay is encrypted, so the
          cost is computed by a Node calc rather than a view — which means it is
          a deliberate step, and the button says so rather than leaving somebody
          to wonder why the P&L did not change. */}
      <button
        type="button"
        onClick={recalculate}
        disabled={recalcing || hasChanges}
        title={hasChanges
          ? 'Save your changes first — the calc reads what is stored, not what is on screen.'
          : 'Recompute salaries into the ledger. Positions planned on a shift are skipped; they are costed by Variable Labour.'}
        className="px-3 py-1 rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {recalcing ? 'Costing…' : 'Recalculate cost'}
      </button>
      <button
        type="button"
        onClick={handleSave}
        disabled={isSaving || !hasChanges}
        className={`px-3 py-1 rounded-md border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
          hasChanges
            ? 'border-emerald-700 bg-emerald-600 text-white font-semibold hover:bg-emerald-700'
            : 'border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
        }`}
      >
        {isSaving ? 'Saving…' : 'Save'}
      </button>
    </>
  ), [handleSave, isSaving, hasChanges, activeIds.length, clearAll, selectedPositions.length,
      recalculate, recalcing]);

  return (
    <div className="h-full flex flex-col font-sans bg-slate-100 dark:bg-slate-900">
      {/* One compact bar: title, the four totals as inline pills, and the add
          toggle. The roll is the point of this page, so the chrome above it is
          kept to a single row and the add form only appears when asked for. */}
      <div className="shrink-0 px-6 pt-4 pb-2">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <h1 className="m-0 text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Structures</h1>
          {ready && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
              {[
                ['Positions', String(summary.positions)],
                ['Vacancies', String(summary.vacancies)],
                ['Monthly CC', fmtR(summary.monthly)],
                ['Annual CC', fmtR(summary.monthly * 12)],
              ].map(([label, value]) => (
                <span key={label} className="inline-flex items-baseline gap-1.5">
                  <span className="uppercase tracking-wide">{label}</span>
                  <span className="font-mono font-bold text-sm text-slate-800 dark:text-slate-100">{value}</span>
                </span>
              ))}
            </div>
          )}
          {/* IS ANY OF THIS ACTUALLY IN THE P&L? Until now the answer was "no,
              none of it", and nothing on this page said so. The roll showed a
              cost of tens of millions and the income statement carried no
              salaries at all. */}
          {ready && costState && (
            costState.positions === 0 ? (
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-amber-300 bg-amber-50 text-amber-700 text-[11px] font-semibold dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300">
                Not in the ledger — press Recalculate cost
              </span>
            ) : costState.unmapped.length > 0 ? (
              <span
                className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-amber-300 bg-amber-50 text-amber-700 text-[11px] font-semibold dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300"
                title={costState.unmapped.slice(0, 12).map((u) => `${u.position_title} (${u.site_code})`).join(', ')}
              >
                {costState.unmapped.length} job title{costState.unmapped.length === 1 ? '' : 's'} post nowhere — set them in GL Mapping
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-emerald-300 bg-emerald-50 text-emerald-700 text-[11px] font-semibold dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-300">
                In the ledger
              </span>
            )
          )}
          {ready && costState && costState.shiftPlannedExcluded > 0 && (
            <span
              className="text-[11px] text-slate-400"
              title="Planned on a shift and costed by Variable Labour. Costing them here as well would count the same person twice."
            >
              {costState.shiftPlannedExcluded} shift-planned position{costState.shiftPlannedExcluded === 1 ? '' : 's'} costed in Manufacturing
            </span>
          )}
          {ready && (
            <button
              type="button"
              onClick={() => setAddOpen((v) => !v)}
              className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
            >
              <Plus className="w-3.5 h-3.5" /> Add position
              {addOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
          )}
        </div>
        {error && <div className="mt-2 p-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded-md">{error}</div>}
      </div>

      {/* Inline add line — Site + Job Title are dropdowns; Grade follows the title. */}
      {ready && addOpen && (
        <div className="shrink-0 px-6 pb-3">
          <div className="flex flex-wrap items-end gap-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3">
            {allSites && (
              <label className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
                Site <span className="text-red-600 dark:text-red-400">*</span>
                <select className={`${inputClass} w-40`} value={entry.site_code}
                  onChange={(e) => setEntry((prev) => ({ ...prev, site_code: e.target.value, fnKey: '', shift_no: '' }))}>
                  <option value="">Select site…</option>
                  {sites.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
                </select>
              </label>
            )}
            <label className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
              Job title <span className="text-red-600 dark:text-red-400">*</span>
              <select className={`${inputClass} w-52`} value={entry.title} onChange={(e) => onEntryTitle(e.target.value)}>
                <option value="">Select job title…</option>
                {jobTitles.map((j) => <option key={j.title} value={j.title}>{j.title}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
              Grade (from title)
              <div className={`${inputClass} w-24 bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400`}>{entry.grade || '—'}</div>
            </label>
            <label className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
              Employee (blank = vacant)
              <input className={`${inputClass} w-44`} value={entry.name} onChange={(e) => setE('name', e.target.value)} placeholder="e.g. Lerato Molefe" />
            </label>
            <label className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
              Pay type
              <select className={`${inputClass} w-28`} value={entry.pay_type} onChange={(e) => setE('pay_type', e.target.value)}>
                <option value="Fixed">Fixed</option>
                <option value="Variable">Variable</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
              Function
              <select className={`${inputClass} w-44`} value={entry.fnKey}
                onChange={(e) => setEntry((prev) => ({ ...prev, fnKey: e.target.value, shift_no: '' }))}>
                <option value="">— none —</option>
                {fnsForSite(allSites ? entry.site_code : selectedSite).map((f) => (
                  <option key={`${f.module_code}|${f.function_code}`} value={fnKeyOf(f.module_code, f.function_code)}>
                    {f.function_name}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
              Shift
              <select className={`${inputClass} w-32`} value={entry.shift_no} disabled={!entry.fnKey}
                onChange={(e) => setE('shift_no', e.target.value)}>
                <option value="">— none —</option>
                {crewsFor(allSites ? entry.site_code : selectedSite,
                  splitFnKey(entry.fnKey).module_code, splitFnKey(entry.fnKey).function_code)
                  .map((s) => <option key={s.shift_no} value={s.shift_no}>{s.shift_name}</option>)}
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200 pb-2">
              <input type="checkbox" className="h-4 w-4 accent-emerald-600" checked={entry.fulltime} onChange={(e) => setE('fulltime', e.target.checked)} />
              Full-time
            </label>
            <button
              type="button"
              onClick={addPosition}
              disabled={!!entryProblem}
              title={entryProblem || 'Add position'}
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md text-white mb-0.5 ${entryProblem ? 'bg-slate-400 dark:bg-slate-600 cursor-not-allowed' : 'bg-emerald-600 hover:bg-emerald-700'}`}
            >
              <Plus className="w-4 h-4" /> Add
            </button>
          </div>
          {jobTitles.length === 0 && (
            <p className="mt-1.5 text-xs text-amber-600">No job titles yet — add them in People → Job Titles first.</p>
          )}
        </div>
      )}

      <div className="flex-1 min-h-0 px-6 pb-6">
        <div className="h-full flex flex-col rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-900">
          {!ready ? (
            <div className="p-10 text-center text-sm text-slate-400">Select a budget version in the top bar to begin.</div>
          ) : isLoading ? (
            <div className="p-4 text-xs text-slate-400">Loading…</div>
          ) : people.length === 0 && activeIds.length === 0 && offset === 0 ? (
            <div className="p-8 text-center text-sm text-slate-400">No positions yet — add one on the line above.</div>
          ) : (
            <>
            {/* See the note in BudgetGrid: the card must be h-full, not
                max-h-full, or flex-1 collapses and the canvas covers the pager. */}
            <div className="flex-1 min-h-0">
            <MantleDataGrid
              gridId="structures"
              fitHeight
              columns={gcols}
              rowCount={rowCount}
              getCellContent={getCellContent}
              onCellEdited={onCellEdited}
              onCellClicked={onCellClicked}
              onHeaderMenuClick={onHeaderMenu}
              onSelectionChanged={onSelectionChanged}
              freezeColumns={allSites ? 2 : 1}
              hint={`Click Job Title, Function or Shift to pick; Status toggles Vacant (basic → grade Mid); Pay Type flips.${allSites ? ' Click Site to move a position — that clears its function and shift.' : ''} Function and Shift come from Variable Labour and belong to the position's own site; changing the function clears the shift. Select rows (drag, or click then shift-click) and use “Assign selected” to set Site, Function or Shift on all of them at once. Header ▾ filters a column. Copy/paste and fill work on Name and the money columns; the picker columns are set by clicking or by Assign selected. Final CC = basic + allowance + medical + UIF (${oncosts.uif_pct}%) + SDL (${oncosts.sdl_pct}%)${oncosts.uif_monthly_cap ? `, UIF capped at ${oncosts.uif_monthly_cap}` : ''}.`}
              toolbar={gridToolbar}
            />
            </div>
            <GridPager
              offset={offset}
              limit={limit}
              rowCount={filtered.length}
              total={pageInfo.total}
              hasMore={pageInfo.hasMore}
              loading={isLoading}
              onOffset={(n) => { if (confirmDiscard()) setOffset(n); }}
              onLimit={(n) => { if (confirmDiscard()) { setLimit(n); setOffset(0); } }}
            />
            </>
          )}
        </div>
      </div>

      {/* Column filter popover — opened from the header ▾ menu. */}
      {filterUI && (
        <FilterPopover
          key={filterUI.colId}
          anchor={filterUI}
          options={filterUI.options}
          filter={filters[filterUI.colId]}
          onApply={(f) => setFilter(filterUI.colId, f)}
          onClose={() => setFilterUI(null)}
        />
      )}

      {/* Bulk assign — set Site / Function / Shift on every selected position. */}
      {bulkOpen && (
        <BulkAssignModal
          count={selectedPositions.length}
          sites={sites}
          functions={catalog.functions}
          shifts={catalog.shifts}
          jobTitles={jobTitles}
          commonSite={commonSite}
          commonFn={commonFn}
          onApply={applyBulk}
          onClose={() => setBulkOpen(false)}
        />
      )}

      {/* Site / Function / Crew picker popover — same plain menu as the job title.
          Each level narrows the next: the site decides which functions exist,
          the function decides which crews do. */}
      {pickEditor && (() => {
        const p = peopleRef.current.find((x) => x.position_id === pickEditor.id);
        if (!p) return null;
        const { kind } = pickEditor;

        const heading = kind === 'site' ? 'Move to site'
          : kind === 'function' ? `Functions at ${siteName(p.site_code)}`
          : `Crews on ${fnNameOf(p)}`;

        const rows =
          kind === 'site' ? sites.map((s) => ({
            key: s.code, main: s.name, aside: s.code,
            onPick: () => onPickSite(pickEditor.id, s.code),
          }))
          : kind === 'function' ? fnsForSite(p.site_code).map((f) => ({
            key: `${f.module_code}|${f.function_code}`, main: f.function_name,
            aside: f.module_name || f.module_code,
            onPick: () => onPickFunction(pickEditor.id, f.module_code, f.function_code),
          }))
          : crewsFor(p.site_code, p.module_code, p.function_code).map((s) => ({
            key: s.shift_no, main: s.shift_name, aside: `S${s.shift_no}`,
            onPick: () => onPickShift(pickEditor.id, Number(s.shift_no)),
          }));

        const empty = kind === 'site' ? 'No sites available.'
          : kind === 'function'
            ? `No functions at ${siteName(p.site_code)} — add them in Distribution or Manufacturing › Variable Labour, or move this position to another site first.`
            : (p.function_code ? 'No crews on this function yet — name them on Variable Labour.'
                               : 'Pick a function first.');

        return (
          <>
            <div className="fixed inset-0 z-40" onMouseDown={() => setPickEditor(null)} />
            <div
              className="fixed z-50 w-64 max-h-72 overflow-auto rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-xl py-1"
              style={{
                top: Math.min(pickEditor.y, window.innerHeight - 300),
                left: Math.min(pickEditor.x, window.innerWidth - 276),
              }}
            >
              <div className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100 dark:border-slate-700">
                {heading}
              </div>
              {kind !== 'site' && (
                <button
                  type="button"
                  onClick={() => (kind === 'function'
                    ? onPickFunction(pickEditor.id, '', '')
                    : onPickShift(pickEditor.id, null))}
                  className="w-full px-3 py-1.5 text-left text-sm text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700"
                >
                  — none —
                </button>
              )}
              {rows.length === 0 ? (
                <div className="px-3 py-2 text-xs text-slate-400">{empty}</div>
              ) : rows.map((r) => (
                <button
                  key={r.key}
                  type="button"
                  onClick={r.onPick}
                  className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
                >
                  <span className="truncate">{r.main}</span>
                  <span className="shrink-0 text-xs text-slate-400">{r.aside}</span>
                </button>
              ))}
              {kind === 'site' && (
                <p className="m-0 px-3 py-1.5 text-[11px] text-amber-600 dark:text-amber-400 border-t border-slate-100 dark:border-slate-700">
                  Moving a position clears its function and shift.
                </p>
              )}
            </div>
          </>
        );
      })()}

      {/* Job-title picker popover — plain menu, opens from the clicked cell. */}
      {titleEditor && (
        <>
          <div className="fixed inset-0 z-40" onMouseDown={() => setTitleEditor(null)} />
          <div
            className="fixed z-50 w-56 max-h-64 overflow-auto rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-xl py-1"
            style={{
              top: Math.min(titleEditor.y, window.innerHeight - 280),
              left: Math.min(titleEditor.x, window.innerWidth - 240),
            }}
          >
            {jobTitles.length === 0 ? (
              <div className="px-3 py-2 text-xs text-slate-400">No job titles — add them in People → Job Titles.</div>
            ) : jobTitles.map((j) => (
              <button
                key={j.title}
                type="button"
                onClick={() => onTitle(titleEditor.id, j.title)}
                className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                <span className="truncate">{j.title}</span>
                <span className="shrink-0 text-xs text-slate-400">{j.grade_code || '—'}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default Structures;
